import { Fragment, useContext, useState } from "react";
import { UserContext } from "../contexts/user";
import { useNavigate, useParams } from "react-router-dom";
import {
  createBlockAnswers,
  updateBlockAnswers,
} from "../axios/axiosFunctions";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { GroupContext } from "../contexts/group";

const QuestionsSection = ({
  questions,
  color,
  blockId,
  buttonsDir,
  currentAnswers,
  setCurrentAnswers,
  is360Assessment,
  isTim = false,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const { currentGroup, setCurrentGroup, setLevel } = useContext(GroupContext);
  const { slugAssessment } = useParams();
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [openDialog, setOpenDialog] = useState(false);
  const [openThankYouDialog, setOpenThankYouDialog] = useState(false);

  const handleAnswers = (e) => {
    const { name, value } = e.target;
    const findAnswer = currentAnswers.find((a) => a.question._id === name);
    const findAnswerIndex = currentAnswers.findIndex(
      (a) => a.question._id === name,
    );
    if (findAnswer) {
      const newCurrentUser = [...currentAnswers];
      newCurrentUser[findAnswerIndex] = {
        ...currentAnswers[findAnswerIndex],
        answer: value,
      };
      setCurrentAnswers(newCurrentUser);
    } else {
      const newCurrentUser = [...currentAnswers];
      newCurrentUser.push({
        question: {
          _id: name,
        },
        id: name,
        answer: value,
      });
      setCurrentAnswers(newCurrentUser);
    }
  };

  const getRadioValue = (q) => {
    const a = currentAnswers.find((a) => a.question._id === q._id);
    return a?.answer || "";
  };

  const validateForm = () => {
    const isValid = true;
    for (let i = 0; i < questions.length; i++) {
      if (!currentAnswers[i]) {
        return !isValid;
      }
    }
    return isValid;
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      setError("Please complete all questions");
      setAlertSeverity("error");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
      return;
    }

    const groupId = currentGroup ? currentGroup?._id : undefined;
    const data = {
      userId: currentUser._id,
      blockId,
      answers: currentAnswers,
      ...(groupId && { groupId }),
      ...(is360Assessment && { to: currentUser._id }),
    };

    let res;

    if (currentAnswers[0]?._id) {
      res = await updateBlockAnswers(data);
    } else {
      res = await createBlockAnswers(data);
    }

    if (!res) {
      setError("Error while saving, please try later");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
    }
    navigate(`/${slugAssessment}/${buttonsDir.after}`);
    window.scrollTo(0, 0);
  };

  const handleSubmitComplete = async () => {
    setOpenDialog(false);
    const isValid = validateForm();
    if (!isValid) {
      setError("Please complete all questions");
      setAlertSeverity("error");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
      return;
    }

    const groupId = currentGroup ? currentGroup?._id : undefined;
    const data = {
      userId: currentUser._id,
      blockId,
      answers: currentAnswers,
      ...(groupId && { groupId }),
      ...(is360Assessment && { to: currentUser._id }),
    };

    let res;

    if (currentAnswers[0]?._id) {
      res = await updateBlockAnswers(data);
    } else {
      res = await createBlockAnswers(data);
    }

    if (!res) {
      setError("Error while saving, please try later");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
    }

    // Show thank you dialog for TIM self assessment
    if (slugAssessment === "tim_self_assessment") {
      setOpenThankYouDialog(true);
      return;
    }

    // Show thank you dialog for 360 assessments (owner)
    if (is360Assessment) {
      setOpenThankYouDialog(true);
      return;
    }

    // For other assessments, navigate directly
    if (currentGroup) {
      setLevel(0);
      setCurrentGroup(undefined);
      localStorage.setItem(
        "groupLL",
        JSON.stringify({ group: undefined, level: 0 }),
      );
    }

    navigate(`/${slugAssessment}`);
  };

  const handleThankYouContinue = () => {
    setOpenThankYouDialog(false);
    if (currentGroup) {
      setLevel(0);
      setCurrentGroup(undefined);
      localStorage.setItem(
        "groupLL",
        JSON.stringify({ group: undefined, level: 0 }),
      );
    }

    // For 360 assessments, navigate to home since there's no immediate report
    if (is360Assessment) {
      navigate("/");
      return;
    }

    navigate(`/${slugAssessment}`);
  };

  return (
    <Container>
      <Box display={"flex"} sx={{ marginTop: "40px", paddingBottom: "48px" }}>
        <form onSubmit={handleSubmit}>
          {questions?.map((q, i) => (
            <Fragment key={i}>
              <Typography
                component={"div"}
                dangerouslySetInnerHTML={{ __html: `${i + 1}. ${q.text}` }}
                variant="body1"
                fontWeight={600}
                color={color}
                sx={{ fontSize: "1.05rem", marginTop: i > 0 ? "28px" : "0px" }}
              />
              {q.typeQ === "mo" && (
                <RadioGroup
                  row
                  value={getRadioValue(q)}
                  onChange={handleAnswers}
                  sx={{
                    justifyContent: "space-between",
                    padding: "4px 10px 0px",
                    marginBottom: "0px",
                  }}
                >
                  {isTim && (
                    <FormControlLabel
                      value="unable"
                      name={q._id}
                      control={<Radio />}
                      label="Unable to Respond"
                    />
                  )}
                  <FormControlLabel
                    value="notConfident"
                    name={q._id}
                    control={<Radio />}
                    label="Not Confident"
                  />
                  <FormControlLabel
                    value="somewhat"
                    name={q._id}
                    control={<Radio />}
                    label="Somewhat Confident"
                  />
                  <FormControlLabel
                    value="confident"
                    name={q._id}
                    control={<Radio />}
                    label="Confident"
                  />
                  <FormControlLabel
                    value="very"
                    name={q._id}
                    control={<Radio />}
                    label="Very Confident"
                  />
                </RadioGroup>
              )}
              {q.typeQ === "op" && (
                <TextField
                  onChange={handleAnswers}
                  fullWidth
                  multiline
                  rows={8}
                  name={q._id}
                />
              )}
            </Fragment>
          ))}
          {showError && (
            <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
              {error}
            </Alert>
          )}
          <Box
            display={"flex"}
            justifyContent={"space-around"}
            sx={{ marginTop: "30px" }}
          >
            {buttonsDir.before ? (
              <Button
                onClick={() => { navigate(`/${slugAssessment}/${buttonsDir.before}`); window.scrollTo(0, 0); }}
                variant="contained"
                color="secondary"
                startIcon={<ArrowBackIcon className="invertIconBlue" />}
                sx={{ color: "white" }}
              >
                BACK
              </Button>
            ) : (
              <Button
                onClick={() => { navigate(`/${slugAssessment}`); window.scrollTo(0, 0); }}
                variant="contained"
                color="secondary"
                startIcon={<ArrowBackIcon className="invertIconBlue" />}
                sx={{ color: "white" }}
              >
                BACK
              </Button>
            )}
            {buttonsDir.after ? (
              <Button
                type="submit"
                variant="contained"
                sx={{ color: "white" }}
                endIcon={<ArrowForwardIcon className="invertIcon" />}
              >
                NEXT
              </Button>
            ) : (
              <Button
                onClick={() => setOpenDialog(true)}
                variant="contained"
                sx={{ color: "white" }}
              >
                COMPLETE
              </Button>
            )}
            <Dialog open={openDialog} onClose={handleDialogClose}>
              <DialogContent>
                <Typography variant="h6" sx={{ textAlign: "center" }}>
                  {is360Assessment
                    ? "Thank you for completing your self-assessment."
                    : "Are you sure you want to SUBMIT your assessment?"}
                </Typography>
                <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
                  {is360Assessment
                    ? "Your responses will be submitted. Once all reviewers have completed their assessments, your results will be generated and you will receive an email notification. Please log back into your account to view the results."
                    : "Once you submit, your report will be generated and you WON'T be able to change your responses."}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    margin: "20px",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    onClick={handleSubmitComplete}
                    variant="contained"
                    sx={{ marginRight: "20px", justifyContent: "space-around" }}
                  >
                    COMPLETE
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleDialogClose}
                    color="customGray"
                  >
                    CANCEL
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
            <Dialog open={openThankYouDialog}>
              <DialogContent>
                {is360Assessment ? (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ textAlign: "center", marginBottom: "16px" }}
                    >
                      Thank you for completing your self-assessment.
                    </Typography>
                    <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
                      Your responses have been submitted. Once all reviewers have
                      completed their assessments, your results will be generated
                      and you will receive an email notification. Please log back
                      into your account to view the results.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ textAlign: "center", marginBottom: "16px" }}
                    >
                      Thank you for completing the Teams in Motion assessment.
                    </Typography>
                    <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
                      This is a team-based assessment. Once all participants have
                      completed the survey, results will be generated and you will
                      receive an email notification. Please log back into your
                      account to view the results.
                    </Typography>
                  </>
                )}
                <Box
                  sx={{
                    display: "flex",
                    margin: "20px",
                    justifyContent: "center",
                  }}
                >
                  <Button onClick={handleThankYouContinue} variant="contained">
                    CONTINUE
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default QuestionsSection;
